import { useState, useEffect } from 'react';
import { X, User, Phone, Mail, MapPin, CreditCard, Briefcase, DollarSign } from 'lucide-react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { apiService } from '../services/api';

interface NoCarsFoundModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BuyerLeadFormData {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  city: string;
  stateProvince: string;
  creditScore: string;
  employmentStatus: string;
  revenueRange: string;
}

export function NoCarsFoundModal({ isOpen, onClose }: NoCarsFoundModalProps) {
  const [formData, setFormData] = useState<BuyerLeadFormData>({
    fullName: '',
    phoneNumber: '',
    emailAddress: '',
    city: '',
    stateProvince: '',
    creditScore: '',
    employmentStatus: '',
    revenueRange: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.stateProvince.trim()) {
      newErrors.stateProvince = 'State/Province is required';
    }

    if (!formData.creditScore.trim()) {
      newErrors.creditScore = 'Credit Score is required';
    } else if (!/^\d+$/.test(formData.creditScore) || parseInt(formData.creditScore) < 300 || parseInt(formData.creditScore) > 850) {
      newErrors.creditScore = 'Please enter a valid credit score (300-850)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.submitBuyerLead({
        full_name: formData.fullName,
        phone_number: formData.phoneNumber,
        email_address: formData.emailAddress,
        city: formData.city,
        state_province: formData.stateProvince,
        credit_score: parseInt(formData.creditScore),
        employment_status: formData.employmentStatus || undefined,
        revenue_range: formData.revenueRange || undefined
      });

      toast.success('Thank you! We will notify you when cars become available in your area.');
      setFormData({
        fullName: '',
        phoneNumber: '',
        emailAddress: '',
        city: '',
        stateProvince: '',
        creditScore: '',
        employmentStatus: '',
        revenueRange: ''
      });
      onClose();
    } catch (error) {
      console.error('Error submitting buyer lead:', error);
      toast.error('Failed to submit your information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" />
      
      <Card className="relative z-10 w-full max-w-2xl mx-4 border-[#f91bdc]/20 shadow-xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative pb-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-gray-100"
          >
            <X size={18} />
          </Button>
          <CardTitle className="text-3xl font-bold text-[#f91bdc] pr-8">
            No Cars Found Near Your Region !
          </CardTitle>
        </CardHeader>
        
        <CardContent className="pb-8 -mt-2">
          <div className="space-y-2 mb-6">
            <p className="text-gray-600">
              We are always working to add more cars, partner with more local dealerships near you, but unfortunately at the moment we still haven't reached your region (State, Province, or City).
            </p>
            <p className="text-gray-600">
              Fill the Info below and we will notify you as soon as new listings from your local dealerships are added.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                  Full Name <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className={`pl-10 ${errors.fullName ? 'border-red-500' : ''}`}
                    placeholder="John Doe"
                  />
                </div>
                {errors.fullName && (
                  <p className="text-sm text-red-500">{errors.fullName}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700">
                  Phone Number <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className={`pl-10 ${errors.phoneNumber ? 'border-red-500' : ''}`}
                    placeholder="+1 (437) 887-1015"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-500">{errors.phoneNumber}</p>
                )}
              </div>

              {/* Email Address */}
              <div className="space-y-2">
                <Label htmlFor="emailAddress" className="text-sm font-medium text-gray-700">
                  Email Address <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="emailAddress"
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                    className={`pl-10 ${errors.emailAddress ? 'border-red-500' : ''}`}
                    placeholder="john.doe@example.com"
                  />
                </div>
                {errors.emailAddress && (
                  <p className="text-sm text-red-500">{errors.emailAddress}</p>
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                  City <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className={`pl-10 ${errors.city ? 'border-red-500' : ''}`}
                    placeholder="Montreal"
                  />
                </div>
                {errors.city && (
                  <p className="text-sm text-red-500">{errors.city}</p>
                )}
              </div>

              {/* State/Province */}
              <div className="space-y-2">
                <Label htmlFor="stateProvince" className="text-sm font-medium text-gray-700">
                  State / Province <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="stateProvince"
                    type="text"
                    value={formData.stateProvince}
                    onChange={(e) => setFormData({ ...formData, stateProvince: e.target.value })}
                    className={`pl-10 ${errors.stateProvince ? 'border-red-500' : ''}`}
                    placeholder="Quebec"
                  />
                </div>
                {errors.stateProvince && (
                  <p className="text-sm text-red-500">{errors.stateProvince}</p>
                )}
              </div>

              {/* Credit Score */}
              <div className="space-y-2">
                <Label htmlFor="creditScore" className="text-sm font-medium text-gray-700">
                  Credit Score <span className="text-[#f91bdc]">*</span>
                </Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="creditScore"
                    type="number"
                    min="300"
                    max="850"
                    value={formData.creditScore}
                    onChange={(e) => setFormData({ ...formData, creditScore: e.target.value })}
                    className={`pl-10 ${errors.creditScore ? 'border-red-500' : ''}`}
                    placeholder="650"
                  />
                </div>
                {errors.creditScore && (
                  <p className="text-sm text-red-500">{errors.creditScore}</p>
                )}
              </div>

              {/* Employment Status */}
              <div className="space-y-2">
                <Label htmlFor="employmentStatus" className="text-sm font-medium text-gray-700">
                  Employment Status
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Select 
                    value={formData.employmentStatus} 
                    onValueChange={(value) => setFormData({ ...formData, employmentStatus: value })}
                  >
                    <SelectTrigger className="pl-10">
                      <SelectValue placeholder="Select employment status" />
                    </SelectTrigger>
                    <SelectContent className="!z-[10000]">
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="self-employed">Self-Employed</SelectItem>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Revenue Range */}
              <div className="space-y-2">
                <Label htmlFor="revenueRange" className="text-sm font-medium text-gray-700">
                  Revenue Range
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Select 
                    value={formData.revenueRange} 
                    onValueChange={(value) => setFormData({ ...formData, revenueRange: value })}
                  >
                    <SelectTrigger className="pl-10">
                      <SelectValue placeholder="Select revenue range" />
                    </SelectTrigger>
                    <SelectContent className="!z-[10000]">
                      <SelectItem value="under-30k">Under $30,000</SelectItem>
                      <SelectItem value="30k-50k">$30,000 - $50,000</SelectItem>
                      <SelectItem value="50k-75k">$50,000 - $75,000</SelectItem>
                      <SelectItem value="75k-100k">$75,000 - $100,000</SelectItem>
                      <SelectItem value="100k-150k">$100,000 - $150,000</SelectItem>
                      <SelectItem value="over-150k">Over $150,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-4">
              <span className="text-[#f91bdc]">*</span> Required Fields to be able to submit
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-gray-300"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-[#f91bdc] hover:bg-[#e015c9] text-white"
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

